import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, useNavigate, useLocation, useMatch, Navigate } from 'react-router-dom';
import { DndContext } from '@dnd-kit/core';

import OptionComboBuilder from './OptionComboBuilder';
import PLChart from './PLChart';
import Toast from './Toast';
import './App.css';
import { API_URLS } from './config';
import { jwtDecode } from 'jwt-decode';
import { googleLogout, useGoogleOneTapLogin } from '@react-oauth/google';

import Spinner from './Spinner';
import Banner from './Banner';
import LoginPage from './LoginPage';
import OptionPackageList from './OptionPackageList';
import PaymentReturn from './PaymentReturn';
import Checkout from './Checkout';
import AccountPage from './AccountPage';
import CustomerSupportPage from './CustomerSupportPage';
import VideoTutorialPage from './VideoTutorialPage';

import fetchWithAuth from './api';

import Topbar from './Topbar';
import NewPackageDialog from './NewPackageDialog';
import ParameterDialog from './ParameterDialog';

import { calculatePL, calculateImpliedVolatility, daysBetween, isValidDate } from './financial';

export const DEFAULT_VOLATILITY = 0;
const DRAFT_PACKAGES_STORAGE_KEY = 'option-builder-draft-packages';

function App() {

  const [plData, setPlData] = useState(null);
  const [chartOptionRequest, setChartOptionRequest] = useState(null);
  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem('user');
    return savedUser ? JSON.parse(savedUser) : null;
  });
  const [userPermissions, setUserPermissions] = useState(null);

  const [lastRequest] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);

  const [comboBuilders, setComboBuilders] = useState([]);
  const [interest, setInterest] = useState(0.0364);
  const [dividendRatio, setDividendRatio] = useState(0.0);
  const [volatility, setVolatility] = useState(DEFAULT_VOLATILITY);
  const [tradingDate, setTradingDate] = useState(new Date().toISOString().split('T')[0]);
  const [valuationDate, setValuationDate] = useState(new Date().toISOString().split('T')[0]);

  const [startPrice, setStartPrice] = useState(9);
  const [endPrice, setEndPrice] = useState(20);
  const [stockSymbol, setStockSymbol] = useState('');
  const [equityPrice, setEquityPrice] = useState(0);

  const [packages, setPackages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [toast, setToast] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const builderMatch = useMatch('/builder/:symbol');
  const builderSymbol = builderMatch?.params?.symbol;

  useEffect(() => {
    localStorage.removeItem(DRAFT_PACKAGES_STORAGE_KEY);
  }, []);

  const fetchPackages = async (permissions) => {
    setError(null);
    setIsLoading(true);
    try {
      const response = await fetchWithAuth(API_URLS.GetOptionPackages, user, permissions);
      if (!response.ok) {
        throw new Error('Failed to fetch option packages from API');
      }
      const data = await response.json();
      setPackages(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUserPermissionsAndPackages = async () => {
    try {
      const response = await fetchWithAuth(API_URLS.GetUserPermissions, user, null);
      if (!response.ok) {
        throw new Error('Failed to fetch user permissions');
      }
      const permissions = await response.json();
      setUserPermissions(permissions);
      await fetchPackages(permissions);
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => {
    if (user) {
      fetchUserPermissionsAndPackages();
    }
  }, [user]);

  const refreshPackages = () => {
    fetchPackages(userPermissions);
  }

  const updateValuationDate = (date) => {
    setValuationDate(date);
    if (selectedPackage && isValidDate(date)) {
      const packageData = JSON.parse(selectedPackage.package);
      packageData.ValuationDate = new Date(date).toISOString();
      const updatedPackage = { ...selectedPackage, package: JSON.stringify(packageData) };
      setSelectedPackage(updatedPackage);
      setPackages(packages.map(p => p.rowKey === updatedPackage.rowKey ? updatedPackage : p));
    }
  };

  const handleLogin = useCallback((response) => {
    const decoded = jwtDecode(response.credential);
    decoded.credential = response.credential;
    localStorage.setItem('user', JSON.stringify(decoded));
    localStorage.setItem('lastActivity', Date.now().toString());
    
    setUser(prevUser => {
      if (!prevUser) {
        navigate('/');
      }
      return decoded;
    });
  }, [navigate]);

  const handleLogout = useCallback(() => {
    googleLogout();
    localStorage.removeItem('user');
    localStorage.removeItem('lastActivity');
    setUser(null);
  }, []);

  useGoogleOneTapLogin({
    onSuccess: handleLogin,
    onError: (error) => console.log('Silent Refresh Error:', error),
    disabled: !user,
    auto_select: true,
    cancel_on_tap_outside: false,
  });

  useEffect(() => {
    if (user) {
      const IDLE_TIMEOUT = 30 * 60 * 1000;
      const REFRESH_BUFFER = 5 * 60 * 1000;
      const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
      
      const updateLastActivity = () => {
        localStorage.setItem('lastActivity', Date.now().toString());
      };

      if (!localStorage.getItem('lastActivity')) {
        updateLastActivity();
      }

      events.forEach(event => window.addEventListener(event, updateLastActivity));

      const checkSession = () => {
        const now = Date.now();
        const lastActivity = parseInt(localStorage.getItem('lastActivity') || now.toString());
        
        if (now - lastActivity > IDLE_TIMEOUT) {
          handleLogout();
          return;
        }

        const decoded = jwtDecode(user.credential);
        const expiresAt = decoded.exp * 1000;

        if (expiresAt - now < REFRESH_BUFFER) {
          if (expiresAt < now) {
            handleLogout();
          }
        }
      };

      const interval = setInterval(checkSession, 60000);

      return () => {
        events.forEach(event => window.removeEventListener(event, updateLastActivity));
        clearInterval(interval);
      };
    }
  }, [user, handleLogout]);

  useEffect(() => {
    const errorHandler = (error) => {
      setToast({ message: `An unexpected error occurred: ${error.message}`, type: 'error' });
    };

    const rejectionHandler = (event) => {
      setToast({ message: `An unexpected error occurred: ${event.reason}`, type: 'error' });
    };

    window.addEventListener('error', errorHandler);
    window.addEventListener('unhandledrejection', rejectionHandler);

    return () => {
      window.removeEventListener('error', errorHandler);
      window.removeEventListener('unhandledrejection', rejectionHandler);
    };
  }, []);

  const handleDragEnd = (event) => {
    const { over, active } = event;
    const activeData = active?.data?.current;
    const overData = over?.data?.current;

    if (!over || activeData?.type !== 'position' || overData?.type !== 'combo') {
      return;
    }

    const sourceBuilderId = activeData.builderId;
    const targetBuilderId = overData.builderId;
    const optionId = activeData.optionId;

    if (sourceBuilderId === targetBuilderId) {
      return;
    }

    const targetBuilder = comboBuilders.find((builder) => builder.id === targetBuilderId);
    const sourceBuilder = comboBuilders.find((builder) => builder.id === sourceBuilderId);
    const movingOption = sourceBuilder?.options.find((option) => option.id === optionId);

    if (
      movingOption?.cardType === 'stock' &&
      targetBuilder?.options.some((option) => option.cardType === 'stock')
    ) {
      setToast({ message: 'The target combo already has a stock position.', type: 'error' });
      return;
    }

    setComboBuilders((prevBuilders) => {
      const currentSourceBuilder = prevBuilders.find((builder) => builder.id === sourceBuilderId);
      const currentTargetBuilder = prevBuilders.find((builder) => builder.id === targetBuilderId);
      const currentMovingOption = currentSourceBuilder?.options.find((option) => option.id === optionId);

      if (!currentSourceBuilder || !currentTargetBuilder || !currentMovingOption) {
        return prevBuilders;
      }

      if (
        currentMovingOption.cardType === 'stock' &&
        currentTargetBuilder.options.some((option) => option.cardType === 'stock')
      ) {
        return prevBuilders;
      }

      return prevBuilders.map((builder) => {
        if (builder.id === targetBuilderId) {
          const copiedOption = {
            ...currentMovingOption,
            id: `${currentMovingOption.cardType}-${Date.now()}`,
            isNew: false,
          };

          return {
            ...builder,
            options: [...builder.options, copiedOption],
          };
        }

        return builder;
      });
    });
  };

  const [isNewPackageDialogOpen, setIsNewPackageDialogOpen] = useState(false);
  const [isParameterDialogOpen, setIsParameterDialogOpen] = useState(false);

  const handleNewPackage = () => {
    setIsNewPackageDialogOpen(true);
  };

  const getPackageName = (pkg) => {
    if (!pkg?.package) {
      return pkg?.rowKey || '';
    }

    try {
      const packageData = JSON.parse(pkg.package);
      return packageData.PackageTitle || packageData.PackageName || pkg.rowKey || '';
    } catch {
      return pkg.rowKey || '';
    }
  };

  const handleCheckPackageExists = (packageName) => {
    const normalizedPackageName = packageName.trim().toUpperCase();
    const existingPackage = packages.find((pkg) => getPackageName(pkg).toUpperCase() === normalizedPackageName);
    if (existingPackage) {
      return `A package with the name '${normalizedPackageName}' already exists. Do you want to overwrite it?`;
    }
    return null;
  };

  const handleCreatePackage = (packageData, overwrite) => {
    const { symbol, packageTitle, description, entityPrice, interestRate, dividendYield, tradingDate } = packageData;
    setPlData(null);
    setComboBuilders([]);
    const existingPackage = packages.find((pkg) => getPackageName(pkg).toUpperCase() === packageTitle.toUpperCase());
    if (existingPackage && !overwrite) {
      return;
    }

    const newPackage = {
      rowKey: packageTitle,
      createdOn: existingPackage?.createdOn || new Date().toISOString(),
      package: JSON.stringify({
        Symbol: symbol,
        PackageTitle: packageTitle,
        Description: description,
        Interest: interestRate,
        DividendRatio: dividendYield,
        Volatility: DEFAULT_VOLATILITY,
        StartPrice: (entityPrice * 0.8).toFixed(2),
        EndPrice: (entityPrice * 1.2).toFixed(2),
        TradingDate: tradingDate,
        ValuationDate: tradingDate,
        EquityPrice: entityPrice,
        OptionCombos: [
          {
            Title: 'My Combo 1',
            StockOptions: [
              {
                Type: 0,
                OptionPrice: entityPrice,
                Contract: 100,
              },
            ],
          },
        ],
      }),
    };

    if (existingPackage && overwrite) {
      setPackages(packages.map((pkg) => (pkg.rowKey === existingPackage.rowKey ? newPackage : pkg)));
      handleSelectPackage(newPackage);
    } else {
      setPackages([...packages, newPackage]);
      handleSelectPackage(newPackage);
    }
    setStockSymbol(symbol);
    navigate(`/builder/${encodeURIComponent(newPackage.rowKey)}`);
    setIsNewPackageDialogOpen(false);
  };

  const handleSelectPackage = (pkg, { skipNavigate = false } = {}) => {
    setPlData(null);
    setChartOptionRequest(null);
    const packageData = JSON.parse(pkg.package);
    setStockSymbol(packageData.Symbol);
    setInterest(packageData.Interest);
    setDividendRatio(packageData.DividendRatio);
    setVolatility(packageData.Volatility ?? DEFAULT_VOLATILITY);
    setStartPrice(packageData.StartPrice);
    setEndPrice(packageData.EndPrice);
    setTradingDate(new Date(packageData.TradingDate).toISOString().split('T')[0]);
    if (packageData.ValuationDate) {
      setValuationDate(new Date(packageData.ValuationDate).toISOString().split('T')[0]);
    } else {
      setValuationDate(new Date(packageData.TradingDate).toISOString().split('T')[0]);
    }
    setEquityPrice(packageData.EquityPrice);

    const newComboBuilders = packageData.OptionCombos.map((combo, index) => ({
      id: Date.now() + index,
      title: combo.Title,
      options: combo.StockOptions.map((option, index2) => {
        if (option.Type === 0) {
          return {
            id: `stock-${Date.now()}-${index2}`,
            price: option.OptionPrice,
            shares: option.Contract,
            cardType: 'stock',
            title: 'Stock'
          };
        } else {
          return {
            id: `option-${Date.now()}-${index2}`,
            strike: option.StrikePrice,
            expiry: new Date(option.ExpiryDate).toISOString().split('T')[0],
            type: option.Type === 1 ? 'Call' : 'Put',
            premium: option.OptionPrice,
            contract: option.Contract,
            impliedVolatility: option.ImpliedVolatility ?? option.impliedVolatility ?? '',
            cardType: 'option',
            seq: `0${index2 + 1}`,
            title: `${option.Type === 1 ? 'Call' : 'Put'} (0${index2 + 1})`
          };
        }
      })
    }));
    setComboBuilders(newComboBuilders);
    setSelectedPackage(pkg);
    if (!skipNavigate) {
      navigate(`/builder/${encodeURIComponent(pkg.rowKey)}`);
    }
  };

  const buildPackageOptionCombos = useCallback((builders) => (
    builders.map((builder) => ({
      Title: builder.title,
      StockOptions: builder.options.map((option) => {
        if (option.cardType === 'stock') {
          return {
            Type: 0,
            OptionPrice: Number(option.price) || 0,
            Contract: Number(option.shares) || 0,
          };
        }

        const calculatedIv = calculateImpliedVolatility(
          Number(option.premium),
          Number(equityPrice),
          Number(option.strike),
          daysBetween(tradingDate, option.expiry) / 365,
          interest,
          dividendRatio,
          String(option.type || '').toLowerCase()
        );
        const impliedVolatility = Number.isFinite(Number(option.impliedVolatility)) && Number(option.impliedVolatility) > 0
          ? Number(option.impliedVolatility)
          : calculatedIv;

        return {
          Type: option.type === 'Call' ? 1 : 2,
          StrikePrice: Number(option.strike) || 0,
          ExpiryDate: option.expiry ? new Date(option.expiry).toISOString() : null,
          OptionPrice: Number(option.premium) || 0,
          Contract: Number(option.contract) || 0,
          ImpliedVolatility: Number.isFinite(Number(impliedVolatility)) ? Number(impliedVolatility) : null,
        };
      }),
    }))
  ), [dividendRatio, equityPrice, interest, tradingDate]);

  useEffect(() => {
    if (!selectedPackage) {
      return;
    }

    const currentPackage = packages.find((pkg) => pkg.rowKey === selectedPackage.rowKey) || selectedPackage;
    if (!currentPackage?.package) {
      return;
    }

    const packageData = JSON.parse(currentPackage.package);
    const nextPackage = {
      ...currentPackage,
      package: JSON.stringify({
        ...packageData,
        Symbol: stockSymbol,
        Interest: interest,
        DividendRatio: dividendRatio,
        Volatility: volatility,
        StartPrice: startPrice,
        EndPrice: endPrice,
        TradingDate: tradingDate,
        ValuationDate: valuationDate,
        EquityPrice: equityPrice,
        OptionCombos: buildPackageOptionCombos(comboBuilders),
      }),
    };

    if (nextPackage.package === currentPackage.package) {
      return;
    }

    setSelectedPackage(nextPackage);
    setPackages((prevPackages) =>
      prevPackages.map((pkg) => (pkg.rowKey === nextPackage.rowKey ? nextPackage : pkg))
    );
  }, [
    buildPackageOptionCombos,
    comboBuilders,
    dividendRatio,
    endPrice,
    equityPrice,
    interest,
    packages,
    selectedPackage,
    startPrice,
    stockSymbol,
    tradingDate,
    valuationDate,
    volatility,
  ]);

  useEffect(() => {
    if (!builderSymbol) {
      return;
    }

    const normalizedSymbol = builderSymbol.toUpperCase();
    if (selectedPackage && selectedPackage.rowKey.toUpperCase() === normalizedSymbol) {
      return;
    }

    const matchingPackage = packages.find(
      (pkg) => pkg.rowKey.toUpperCase() === normalizedSymbol
    );

    if (matchingPackage) {
      handleSelectPackage(matchingPackage, { skipNavigate: true });
    } else if (!isLoading) {
      setToast({ message: `Option package '${normalizedSymbol}' was not found.`, type: 'error' });
    }
  }, [builderSymbol, packages, selectedPackage, isLoading]);

  const addOptionBuilder = () => {
    const nextComboNumber = comboBuilders.length + 1;
    setComboBuilders([...comboBuilders, { id: Date.now(), title: `Option Combo ${nextComboNumber}`, options: [] }]);
  };

  const handleCopyBuilder = (builderId) => {
    setComboBuilders((prevBuilders) => {
      const sourceIndex = prevBuilders.findIndex((builder) => builder.id === builderId);
      if (sourceIndex === -1) {
        return prevBuilders;
      }

      const sourceBuilder = prevBuilders[sourceIndex];
      const timestamp = Date.now();
      const copiedBuilder = {
        ...sourceBuilder,
        id: timestamp,
        title: `${sourceBuilder.title} Copy`,
        options: sourceBuilder.options.map((option, index) => ({
          ...option,
          id: `${option.cardType}-${timestamp}-${index}`,
          isNew: false,
        })),
      };

      return [
        ...prevBuilders.slice(0, sourceIndex + 1),
        copiedBuilder,
        ...prevBuilders.slice(sourceIndex + 1),
      ];
    });
  };

  const handleRemoveOption = (builderId, optionId) => {
    setComboBuilders(
      comboBuilders.map((builder) =>
        builder.id === builderId
          ? { ...builder, options: builder.options.filter((o) => o.id !== optionId) }
          : builder
      )
    );
  };

  const handleUpdateOption = (builderId, optionId, field, value) => {
    let parsedValue = value;
    if (field === 'contract') {
      if (value === '' || value === '-') {
        parsedValue = value;
      } else {
        parsedValue = parseInt(value, 10);
        if (isNaN(parsedValue)) {
          parsedValue = 0;
        }
      }
    } else if (['premium', 'strike', 'price', 'shares', 'impliedVolatility'].includes(field)) {
      const stringValue = String(value);
      if (stringValue === '' || stringValue.endsWith('.')) {
        parsedValue = stringValue;
      } else {
        parsedValue = parseFloat(stringValue);
        if (isNaN(parsedValue)) {
          parsedValue = 0;
        }
      }
    }
    setComboBuilders(
      comboBuilders.map((builder) =>
        builder.id === builderId
          ? {
              ...builder,
              options: builder.options.map((o) =>
                o.id === optionId ? { ...o, [field]: parsedValue } : o
              ),
            }
          : builder
      )
    );
  };

  const handleUpdateTitle = (builderId, title) => {
    setComboBuilders(
      comboBuilders.map((builder) =>
        builder.id === builderId ? { ...builder, title } : builder
      )
    );
  };

  const handleReplaceOption = (builderId, optionId, newOption) => {
    setComboBuilders(
      comboBuilders.map((builder) =>
        builder.id === builderId
          ? {
              ...builder,
              options: builder.options.map((o) =>
                o.id === optionId ? newOption : o
              ),
            }
          : builder
      )
    );
  };

  const handleRemoveBuilder = (builderId) => {
    setComboBuilders(comboBuilders.filter((builder) => builder.id !== builderId));
  };

  const handleDisplayPLChart = () => {
    for (const builder of comboBuilders) {
      if (!builder.title) {
        setToast({ message: 'Combo title is required.', type: 'error' });
        return;
      }
      if (builder.options.length === 0) {
        setToast({ message: 'Each combo must have at least one item.', type: 'error' });
        return;
      }
      for (const option of builder.options) {
        if (option.cardType === 'option') {
          if (!option.strike || option.strike <= 0) {
            setToast({ message: 'Strike price is required and must be greater than 0.', type: 'error' });
            return;
          }
          if (!option.premium || option.premium <= 0) {
            setToast({ message: 'Premium is required and must be greater than 0.', type: 'error' });
            return;
          }
          if (!option.impliedVolatility || Number(option.impliedVolatility) <= 0) {
            setToast({ message: 'Implied volatility is required and must be greater than 0.', type: 'error' });
            return;
          }
        }
      }
    }
    setIsParameterDialogOpen(true);
  };

  const confirmAndDrawChart = async () => {
    if (!stockSymbol) {
      setToast({ message: 'Equity Symbol is required.', type: 'error' });
      return;
    }
    if (!tradingDate || !isValidDate(tradingDate)) {
      setToast({ message: 'Valid Trading Date is required.', type: 'error' });
      return;
    }
    if (!valuationDate || !isValidDate(valuationDate)) {
      setToast({ message: 'Valid Valuation Date is required.', type: 'error' });
      return;
    }
    if (new Date(valuationDate) < new Date(tradingDate)) {
      setToast({ message: 'Valuation Date must be greater than or equal to Trading Date.', type: 'error' });
      return;
    }
    if (interest === null || interest < 0) {
      setToast({ message: 'Interest Rate is required and must be greater than or equal to 0.', type: 'error' });
      return;
    }
    if (startPrice === null) {
      setToast({ message: 'Chart Range From is required.', type: 'error' });
      return;
    }
    if (endPrice === null || endPrice <= startPrice) {
      setToast({ message: 'Chart Range To is required and must be greater than Chart Range From.', type: 'error' });
      return;
    }

    for (const builder of comboBuilders) {
      for (const option of builder.options) {
        if (option.cardType === 'option') {
          if (!option.expiry || !isValidDate(option.expiry)) {
            setToast({ message: 'Valid Expiry date is required.', type: 'error' });
            return;
          }
        }
      }
    }

    setIsParameterDialogOpen(false);
    drawChart();
  }

  const drawChart = async () => {
    let packageName = stockSymbol;
    let packageDescription = '';

    if (selectedPackage?.package) {
      try {
        const packageData = JSON.parse(selectedPackage.package);
        packageName = packageData.PackageTitle || packageData.PackageName || packageData.Symbol || stockSymbol;
        packageDescription = packageData.Description || '';
      } catch {
        packageName = stockSymbol;
        packageDescription = '';
      }
    }

    const newOptionCombos = comboBuilders.map(builder => {
      const newStockOptions = builder.options.map(option => {
        if (option.cardType === 'option') {
          const days = daysBetween(tradingDate, option.expiry);
          const T = days / 365;
          const computedIv = calculateImpliedVolatility(
            option.premium,
            equityPrice,
            option.strike,
            T,
            interest,
            dividendRatio,
            option.type.toLowerCase()
          );
          const iv = Number.isFinite(Number(option.impliedVolatility)) && Number(option.impliedVolatility) > 0
            ? Number(option.impliedVolatility)
            : computedIv;
          return {
            ...option,
            impliedVolatility: iv,
            ticker: stockSymbol,
            strikePrice: option.strike,
            expiryDate: new Date(option.expiry).toISOString(),
            type: option.type === 'Call' ? 1 : 2,
            optionPrice: option.premium,
          };
        } else {
          return {
            ...option,
            ticker: stockSymbol,
            strikePrice: 0,
            expiryDate: new Date(new Date().setFullYear(new Date().getFullYear() + 5)).toISOString(),
            type: 0,
            optionPrice: option.price,
            contract: Number(option.shares),
          };
        }
      });
      return {
        ...builder,
        stockOptions: newStockOptions,
      };
    });

    const optionRequest = {
      EquityPrice: equityPrice,
      symbol: stockSymbol,
      packageName: packageName,
      description: packageDescription,
      interest: interest,
      dividendRatio: dividendRatio,
      volatilityChange: volatility,
      startPrice: startPrice,
      endPrice: endPrice,
      tradingDate: new Date(tradingDate).toISOString(),
      valuationDate: new Date(valuationDate).toISOString(),
      optionCombos: newOptionCombos.map(builder => ({
        title: builder.title,
        stockOptions: builder.stockOptions.map(item => {
          if (item.cardType === 'option') {
            return {
              ticker: item.ticker,
              strikePrice: item.strikePrice,
              expiryDate: item.expiryDate,
              type: item.type,
              optionPrice: item.optionPrice,
              contract: item.contract,
              impliedVolatility: item.impliedVolatility,
            };
          } else {
            return {
              ticker: item.ticker,
              strikePrice: item.strikePrice,
              expiryDate: item.expiryDate,
              type: item.type,
              optionPrice: item.optionPrice,
              contract: item.contract,
            };
          }
        }),
      })),
    };

    if (JSON.stringify(optionRequest) === JSON.stringify(lastRequest)) {
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetchWithAuth(API_URLS.OPTION_CALCULATOR, user, userPermissions, {
        method: 'POST',
        body: JSON.stringify(optionRequest),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch P/L data from API');
      }

      const data = calculatePL(optionRequest);
      setPlData(data);
      setChartOptionRequest(optionRequest);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }

  if (!user) {
    return <LoginPage handleLogin={handleLogin} />;
  }

  return (
    <DndContext onDragEnd={handleDragEnd}>
      <Banner user={user} handleLogout={handleLogout} />
      <div className="app-container-column">
        <Topbar 
            pathname={location.pathname}
            handleNewPackage={handleNewPackage}
            addOptionBuilder={addOptionBuilder}
            handleDisplayPLChart={handleDisplayPLChart}
        />
        <NewPackageDialog
          isOpen={isNewPackageDialogOpen}
          onClose={() => setIsNewPackageDialogOpen(false)}
          onCreatePackage={handleCreatePackage}
          onCheckPackageExists={handleCheckPackageExists}
          setToast={setToast}
        />
        <ParameterDialog
          isOpen={isParameterDialogOpen}
          onClose={() => setIsParameterDialogOpen(false)}
          onConfirm={confirmAndDrawChart}
          optionRequest={{
            optionCombos: comboBuilders.map(builder => ({
              ...builder,
              stockOptions: builder.options.map(option => ({
                ...option,
                impliedVolatility: option.cardType === 'option' && typeof option.type === 'string'
                  ? (
                      Number.isFinite(Number(option.impliedVolatility)) && Number(option.impliedVolatility) > 0
                        ? Number(option.impliedVolatility)
                        : calculateImpliedVolatility(
                            option.premium,
                            equityPrice,
                            option.strike,
                            (daysBetween(tradingDate, option.expiry)) / 365,
                            interest,
                            dividendRatio,
                            option.type.toLowerCase()
                          )
                    )
                  : null
              }))
            }))
          }}
          setStockSymbol={setStockSymbol}
          tradingDate={tradingDate}
          setTradingDate={setTradingDate}
          valuationDate={valuationDate}
          setValuationDate={updateValuationDate}
          interest={interest}
          setInterest={setInterest}
          dividendRatio={dividendRatio}
          setDividendRatio={setDividendRatio}
          volatility={volatility}
          setVolatility={setVolatility}
          startPrice={startPrice}
          setStartPrice={setStartPrice}
          endPrice={endPrice}
          setEndPrice={setEndPrice}
          showPriceRangeControls={true}
        />
        <div className="main-content">
          {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
          {isLoading && <Spinner />}
          <Routes>
            <Route path="/return" element={<PaymentReturn setToast={setToast} />} />
            <Route path="/checkout" element={<Checkout user={user} userPermissions={userPermissions} />} />
            <Route path="/account" element={<AccountPage user={user} userPermissions={userPermissions} />} />
            <Route path="/support" element={<CustomerSupportPage user={user} userPermissions={userPermissions} setToast={setToast} />} />
            <Route path="/tutorials" element={<VideoTutorialPage />} />
            <Route path="/" element={<OptionPackageList onSelectPackage={handleSelectPackage} user={user} packages={packages} isLoading={isLoading} error={error} onRefresh={refreshPackages} handleNewPackage={handleNewPackage} userPermissions={userPermissions} />} />
            <Route path="/builder/:symbol" element={
              <div className="flex-container">
                <div className="option-combo-builder-container">
                  <OptionComboBuilder
                    stockSymbol={stockSymbol}
                    equityPrice={equityPrice}
                    interest={interest}
                    dividendRatio={dividendRatio}
                    tradingDate={tradingDate}
                    comboBuilders={comboBuilders}
                    setComboBuilders={setComboBuilders}
                    addOptionBuilder={addOptionBuilder}
                    handleRemoveOption={handleRemoveOption}
                    handleUpdateOption={handleUpdateOption}
                    handleUpdateTitle={handleUpdateTitle}
                    handleReplaceOption={handleReplaceOption}
                    handleCopyBuilder={handleCopyBuilder}
                    handleRemoveBuilder={handleRemoveBuilder}
                    handleDisplayPLChart={handleDisplayPLChart}
                    setIsLoading={setIsLoading}
                    isLoading={isLoading}
                    setToast={setToast}
                  />
                  {plData && <PLChart datasets={plData} optionRequest={chartOptionRequest} />}
                </div>
              </div>
            } />
            <Route path="/builder" element={<Navigate to={selectedPackage ? `/builder/${encodeURIComponent(selectedPackage.rowKey)}` : '/'} replace />} />
          </Routes>
        </div>
      </div>
    </DndContext>
  );
}

export default App;
